import React, { useCallback, useEffect, useRef, useState } from 'react';
import AddTicketInput from '../AddTicketInput/AddTicketInput';
import { useDispatch, useSelector } from 'react-redux';
import { getTranslation } from '../../../../utils/translation';
import useClickOutside from '../../../../hooks/useClickOutside';
import { setEventSearchProps } from '../../../../store/actions/eventDetails';
import { getMoreMarketsUrl } from '../../../../utils/helpers';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import styles from './EventsSearch.module.scss';

const EventsSearch = ({ events, searchValue }) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const isEventDetailsLoading = useSelector(state => state.eventDetails.loading);
    const { isVisible } = useSelector(state => state.eventDetails.eventSearch);

    const [isListVisible, setIsListVisible] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isListOnFocus, setIsListOnFocus] = useState(false);

    const inputRef = useRef(null);
    const listRef = useRef(null);

    const closeEventsList = useCallback(() => {
        setIsListVisible(false);
        setIsListOnFocus(false);
    }, []);

    useClickOutside(listRef, closeEventsList);

    useEffect(() => {
        if (events.length > 0) {
            setIsListVisible(true);
        } else {
            setIsListVisible(false);
            setIsListOnFocus(false);
        }
    }, [events]);

    const toggleVirtualKeyboard = () => {
        dispatch(setEventSearchProps({ isVisible: !isVisible }));
    };

    const onInputChange = event => {
        const { value } = event.target;
        dispatch(setEventSearchProps({ value }));
    };

    const onEventSelect = event => {
        setSelectedEvent(event);
        const redirectUrl = getMoreMarketsUrl({ event });
        history.push(redirectUrl);
    };

    const renderLoadingMarketsMessage = id => {
        if (selectedEvent && selectedEvent.id === id && isEventDetailsLoading) {
            return <span className={styles.loadingMarkets}>Loading markets...</span>;
        }
        return null;
    };

    const onInputKeyDown = e => {
        if (listRef && listRef.current) {
            if (e.keyCode === 40) {
                if (listRef.current.children[1]) {
                    setIsListOnFocus(true);
                    listRef.current.children[1].focus();
                }
            }

            if (e.keyCode === 13) {
                onEventSelect(events[0]);
            }
        }
    };

    const onListItemKeyDown = (e, selectedItem) => {
        const active = document.activeElement;

        if (e.keyCode === 40 && active.nextSibling) {
            active.nextSibling.focus();
        }

        if (e.keyCode === 38 && active.previousSibling) {
            active.previousSibling.focus();
        }

        if (e.keyCode === 38 && !active.previousSibling) {
            setIsListOnFocus(false);
            inputRef.current.focus();
            setTimeout(() => {
                inputRef.current.setSelectionRange(1000, 1000);
            }, 0);
        }

        if (e.keyCode === 13) {
            onEventSelect(selectedItem);
        }
    };

    return (
        <div className={styles.searchInputWrapper}>
            <AddTicketInput
                ref={inputRef}
                placeholder={getTranslation('input.placeholder.eventIdOrEventName')}
                inputValue={searchValue}
                onChange={onInputChange}
                setShowKeyboard={toggleVirtualKeyboard}
                onKeyPress={onInputKeyDown}
            />
            {isListVisible ? (
                <div ref={listRef} className={styles.searchResult}>
                    {events.map((event, index) => (
                        <div
                            key={event.id}
                            className={classnames(styles.item, {
                                [styles.highlight]: index === 0 && !isListOnFocus,
                            })}
                            onClick={() => onEventSelect(event)}
                            tabIndex={index}
                            onKeyDown={e => onListItemKeyDown(e, event)}
                        >
                            {event.eventName}
                            {renderLoadingMarketsMessage(event.id)}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default EventsSearch;
